import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Avatar,
  Box,
  Alert,
  Pagination,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { supabaseService } from '../services/supabaseService';

interface Report {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  reason: string;
  description?: string;
  status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
  timestamp: number;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  reporter?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
  reported_user?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
}

interface ReportsStatistics {
  total: number;
  pending: number;
  reviewing: number;
  resolved: number;
  dismissed: number;
}

const ReportsPage: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [statistics, setStatistics] = useState<ReportsStatistics>({
    total: 0,
    pending: 0,
    reviewing: 0,
    resolved: 0,
    dismissed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const statusColors = {
    pending: 'warning',
    reviewing: 'info',
    resolved: 'success',
    dismissed: 'default',
  } as const;

  const statusLabels = {
    pending: 'Beklemede',
    reviewing: 'İnceleniyor',
    resolved: 'Çözüldü',
    dismissed: 'Reddedildi',
  };

  const loadReports = useCallback(async (currentPage = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const limit = 20;
      const offset = (currentPage - 1) * limit;

      console.log('📋 Loading reports...', { currentPage, limit, offset, statusFilter });

      const reportsData = await supabaseService.getAllReports({
        limit,
        offset,
        statusFilter: statusFilter || undefined,
      });

      console.log('📊 Reports loaded:', reportsData);
      setReports(reportsData);

      // Calculate total pages (rough estimate)
      setTotalPages(Math.max(1, Math.ceil(reportsData.length < limit ? currentPage : currentPage + 1)));
    } catch (err: any) {
      console.error('❌ Error loading reports:', err);
      setError(err.message || 'Reports yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await supabaseService.getReportsStatistics();
      console.log('📈 Statistics loaded:', stats);
      setStatistics(stats);
    } catch (err) {
      console.error('❌ Error loading statistics:', err);
    }
  }, []);

  useEffect(() => {
    loadReports(page);
    loadStatistics();
  }, [page, loadReports, loadStatistics]);

  const handleStatusUpdate = async () => {
    if (!selectedReport || !newStatus) return;

    try {
      setLoading(true);
      const success = await supabaseService.updateReportStatus(
        selectedReport.id,
        newStatus,
        adminNotes
      );

      if (success) {
        setUpdateDialogOpen(false);
        setSelectedReport(null);
        setNewStatus('');
        setAdminNotes('');
        await loadReports(page);
        await loadStatistics();
      } else {
        setError('Report durumu güncellenemedi');
      }
    } catch (err: any) {
      console.error('❌ Error updating report:', err);
      setError(err.message || 'Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!window.confirm('Bu raporu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      const success = await supabaseService.deleteReport(reportId);

      if (success) {
        await loadReports(page);
        await loadStatistics();
      } else {
        setError('Report silinemedi');
      }
    } catch (err: any) {
      console.error('❌ Error deleting report:', err);
      setError(err.message || 'Silme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = (report: Report) => {
    setSelectedReport(report);
    setNewStatus(report.status);
    setAdminNotes(report.admin_notes || '');
    setUpdateDialogOpen(true);
  };

  const openDetailDialog = (report: Report) => {
    setSelectedReport(report);
    setDetailDialogOpen(true);
  };

  const getPhotoUrl = (photos?: string[]) => {
    return photos && photos.length > 0 ? photos[0] : '/default-avatar.png';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  if (loading && reports.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Reports yükleniyor...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <FlagIcon sx={{ mr: 2, color: 'primary.main' }} />
          📋 Raporlar
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadReports(page);
            loadStatistics();
          }}
          disabled={loading}
        >
          Yenile
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box 
        sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 3,
          mb: 4
        }}
      >
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Toplam Report
            </Typography>
            <Typography variant="h4" component="div">
              {statistics.total}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Beklemede
            </Typography>
            <Typography variant="h4" component="div" color="warning.main">
              {statistics.pending}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
            İnceleniyor
            </Typography>
            <Typography variant="h4" component="div" color="info.main">
              {statistics.reviewing}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Çözüldü
            </Typography>
            <Typography variant="h4" component="div" color="success.main">
              {statistics.resolved}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Reddedildi
            </Typography>
            <Typography variant="h4" component="div" color="text.secondary">
              {statistics.dismissed}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status Filtresi</InputLabel>
            <Select
          value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              label="Status Filtresi"
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="pending">Beklemede</MenuItem>
              <MenuItem value="reviewing">İnceleniyor</MenuItem>
              <MenuItem value="resolved">Çözüldü</MenuItem>
              <MenuItem value="dismissed">Reddedildi</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Reports Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rapor Eden</TableCell>
              <TableCell>Rapor Edilen</TableCell>
              <TableCell>Sebep</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Tarih</TableCell>
              <TableCell align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Hiç report bulunamadı
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getPhotoUrl(report.reporter?.photos)}
                        sx={{ width: 32, height: 32, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {report.reporter?.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {report.reporter?.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getPhotoUrl(report.reported_user?.photos)}
                        sx={{ width: 32, height: 32, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {report.reported_user?.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {report.reported_user?.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {report.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[report.status]}
                      color={statusColors[report.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(report.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Detayları Görüntüle">
                      <IconButton
                        onClick={() => openDetailDialog(report)}
                        size="small"
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Durum Güncelle">
                      <IconButton
                        onClick={() => openUpdateDialog(report)}
                        size="small"
                        color="info"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton
                        onClick={() => handleDeleteReport(report.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Update Status Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Durumunu Güncelle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Yeni Durum</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Yeni Durum"
            >
              <MenuItem value="pending">Beklemede</MenuItem>
              <MenuItem value="reviewing">İnceleniyor</MenuItem>
              <MenuItem value="resolved">Çözüldü</MenuItem>
              <MenuItem value="dismissed">Reddedildi</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Admin Notları"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)}>İptal</Button>
          <Button onClick={handleStatusUpdate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Report Detayları</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Rapor Bilgileri
              </Typography>
              <Typography><strong>ID:</strong> {selectedReport.id}</Typography>
              <Typography><strong>Sebep:</strong> {selectedReport.reason}</Typography>
              <Typography><strong>Açıklama:</strong> {selectedReport.description || 'Yok'}</Typography>
              <Typography><strong>Durum:</strong> 
                <Chip
                  label={statusLabels[selectedReport.status]}
                  color={statusColors[selectedReport.status]}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography><strong>Oluşturma Tarihi:</strong> {formatDate(selectedReport.created_at)}</Typography>
              <Typography><strong>Güncelleme Tarihi:</strong> {formatDate(selectedReport.updated_at)}</Typography>
              {selectedReport.admin_notes && (
                <Typography><strong>Admin Notları:</strong> {selectedReport.admin_notes}</Typography>
              )}
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: 3, 
                mt: 3 
              }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Rapor Eden Kullanıcı
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={getPhotoUrl(selectedReport.reporter?.photos)}
                      sx={{ width: 64, height: 64, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">{selectedReport.reporter?.name || 'Unknown User'}</Typography>
                      <Typography color="textSecondary">{selectedReport.reporter?.email || 'No email'}</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Rapor Edilen Kullanıcı
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={getPhotoUrl(selectedReport.reported_user?.photos)}
                      sx={{ width: 64, height: 64, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">{selectedReport.reported_user?.name || 'Unknown User'}</Typography>
                      <Typography color="textSecondary">{selectedReport.reported_user?.email || 'No email'}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
          <Button
            onClick={() => {
              setDetailDialogOpen(false);
              if (selectedReport) openUpdateDialog(selectedReport);
            }}
            variant="contained"
          >
            Düzenle
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsPage; 